import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Typography, Box, CircularProgress, Card, CardContent, FormControlLabel, Checkbox, Button } from "@material-ui/core";
import pick from "lodash/pick";
import { useFormik } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useParams } from "react-router-dom";
import { Input, Autocomplete } from "components/Shared/mui-formik-inputs";
import { UserSchema } from "constants/validation-schemas";
import { UserAllowedFields } from "constants/forms-submit-allowed-fields";
import { PATHS } from "util/appConstants";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";
import { getAllRoles, selectRoles, selectRolesStatus } from "redux/slices/rolesSlice";
import { useDispatch, useSelector } from "react-redux";
import { getAllPermissions, selectPermissions } from "redux/slices/permissionsSlice";
import { selectUser } from "redux/slices/userSlice";
import { convertPermissionsToArray } from "util/helpers";
import { upperFirst } from "lodash";
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import ClearAllIcon from "@material-ui/icons/ClearAll";

import PermissionsManager from "components/Permissions/PermissionsManager";

const useStyles = makeStyles({
  _heading: {
    font: "normal normal normal 28px/40px Questrial",
    color: "#121212",
  },
  _icons: {
    color: "#ADADAD",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    margin: "0 16px 0 0px",
    fontSize: "35px",
  },

  _save: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#6F9CEB",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _close: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#525252",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  card: {
    height: 'auto',
  },
  cardContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "8px 16px !important",
    margin: "0",
    '&:last-child': {
      paddingBottom: "8px !important",
    },
  },
  _subheading: {
    font: "normal normal 500 22px/32px Roboto",
    color: " #121212",
    marginTop: "44px",
    marginBottom: "40px",
  },
  _dflex: {
    display: "flex",
    alignItems: "center",
  },
  resetButton: {
    marginLeft: "16px",
    backgroundColor: "#f5f5f5",
    color: "#525252",
    "&:hover": {
      backgroundColor: "#e0e0e0",
    },
  },
});
const UserForm = ({ initialValues, onSubmit, action, suppliers, supplierLocations }) => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();

  const loading = useSelector(selectRolesStatus);
  const roles = useSelector(selectRoles);
  const permissions = useSelector(selectPermissions);
  const [flatPermissions, setFlatPermissions] = useState({});
  const buildPermissionsPayload = (flatPermissions, permissionsList, { includeFalse = false } = {}) => {
    const result = {};
    if (!permissionsList || !permissionsList.length) return result;

    permissionsList.forEach((perm) => {
      const module = perm.module.toLowerCase().replace(/\s+/g, "_");
      const name = perm.name;

      const value = !!flatPermissions?.[perm.id];
      if (!includeFalse && !value) return;

      if (!result[module]) result[module] = {};
      result[module][name] = value;
    });
    return result;
  };
  const [rolePermissions, setRolePermissions] = useState({});
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  // Add a new state to track if permissions have been manually modified
  const [permissionsModified, setPermissionsModified] = useState(false);
  // Add a new state to track when permissions were last reset
  const [lastResetTime, setLastResetTime] = useState(null);
  // Add a state to track the last role applied
  const [lastAppliedRoleId, setLastAppliedRoleId] = useState(null);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: action === "EDIT"
      ? UserSchema.omit(["password"])
      : UserSchema,
    initialValues: {
      email: "",
      password: "",
      name: "",
      surname: "" || null,
      supplier_id: "",
      supplier_location_id: "",
      role_id: "",
      active: "",
      permissions: flatPermissions,
      ...initialValues,
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const filteredValues = pick(values, UserAllowedFields);
        if (action === "EDIT" && !values.password) {
          delete filteredValues.password;
        }
        filteredValues.permissions = buildPermissionsPayload(values.permissions, permissions, {
          includeFalse: action === "EDIT",
        });
        await onSubmit(filteredValues);
      } catch (err) {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    handleChange,
    setFieldValue,
    errors,
    handleSubmit,
    submitCount,
    isValid,
    isSubmitting,
    setFieldTouched,
  } = formik;
  let { handleBlur } = formik;
  if (!submitCount) {
    handleBlur = null;
  }
  const groupedPermissions = useMemo(() => {
    return permissions.reduce((acc, perm) => {
      if (!acc[perm.module]) acc[perm.module] = [];
      acc[perm.module].push(perm);
      return acc;
    }, {});
  }, [permissions]);

  const closeCustomerHandler = () => {
    action === "ADD"
      ? history.push(PATHS.admin.users)
      : history.push(PATHS.admin.usersDetail.replace(":id", id));
  };

  const cancelButtonRef = useRef(null);
  const saveButtonRef = useRef(null);

  const getRolePermissionsMap = (selectedRoleId) => {
    const permissionsValue = {};
    permissions.forEach((perm) => {
      permissionsValue[perm.id] = false;
    });

    if (!selectedRoleId) return permissionsValue;

    const selectedRole = roles.find((role) => role.id === selectedRoleId);
    if (!selectedRole) return permissionsValue;

    const permsArray =
      selectedRole?.RoleHasPermissions ||
      selectedRole?.roleHasPermissions ||
      selectedRole?.Permissions ||
      [];

    permsArray.forEach((perm) => {
      const permId = perm.permission_id !== undefined ? perm.permission_id : perm.id;
      const permValue = perm.value !== undefined ? Boolean(perm.value) : true;
      if (permId !== undefined && permId !== null) {
        permissionsValue[permId] = permValue;
      }
    });

    return permissionsValue;
  };

  const handleRoleChange = (selectedRoleId) => {
    setFieldValue("role_id", selectedRoleId);
    if (!selectedRoleId) return;

    const rolePerms = getRolePermissionsMap(selectedRoleId);
    setRolePermissions(rolePerms);
    setFieldValue("permissions", rolePerms);
    setLastAppliedRoleId(selectedRoleId);
    setPermissionsModified(false);
  };

  const handleResetPermissions = () => {
    const roleId = values.role_id;
    const rolePerms = getRolePermissionsMap(roleId);
    setRolePermissions(rolePerms);
    setFieldValue("permissions", rolePerms);
    setLastResetTime(Date.now());
    setPermissionsModified(false);
  };
  const handlePermissionsChange = (updated) => {
    setRolePermissions(updated);
    setFieldValue("permissions", updated);
    setPermissionsModified(true);
  };

  useEffect(() => {
    if (!loading) {
      dispatch(getAllRoles());
      dispatch(getAllPermissions());
    }
  }, [dispatch]);

  const normalizeKey = (str) => (str || "").toString().toLowerCase().replace(/[\s_-]+/g, "");

  const getPermissionValueFromNested = (nestedPermissions, perm) => {
    if (!nestedPermissions || typeof nestedPermissions !== "object") return false;

    const targetModuleNorm = normalizeKey(perm.module);
    const targetNameNorm = normalizeKey(perm.name);

    for (const [modKey, modVal] of Object.entries(nestedPermissions)) {
      if (normalizeKey(modKey) === targetModuleNorm && modVal && typeof modVal === "object") {
        for (const [nameKey, val] of Object.entries(modVal)) {
          if (normalizeKey(nameKey) === targetNameNorm) {
            return Boolean(val);
          }
        }
      }
    }
    return false;
  };

  const flattenPermissions = (nestedPermissions, permissionsList) => {
    const result = {};
    if (!permissionsList || !permissionsList.length) return result;

    permissionsList.forEach((perm) => {
      result[perm.id] = getPermissionValueFromNested(nestedPermissions, perm);
    });

    return result;
  };

  useEffect(() => {
    if (action === "EDIT" && permissions.length && initialValues?.permissions) {
      // Convert the nested permissions to a flat structure
      const flatPerms = flattenPermissions(initialValues.permissions, permissions);

      // Set the flat permissions to state and form
      setRolePermissions(flatPerms);
      setFieldValue("permissions", flatPerms);
    }
  }, [action, initialValues?.permissions, permissions, setFieldValue]);

  const handleSupplierChange = (selectedSupplierId) => {
    setFieldValue("supplier_id", selectedSupplierId);
    setFieldValue("supplier_location_id", "");
    if (selectedSupplierId) {
      const filtered = supplierLocations.filter(
        location => location.supplier_id === selectedSupplierId
      );
      setFilteredLocations(filtered);
    } else {
      setFilteredLocations([]);
    }
  };

  useEffect(() => {
    if (values.supplier_id && supplierLocations.length) {
      const filtered = supplierLocations.filter(
        location => location.supplier_id === values.supplier_id
      );
      setFilteredLocations(filtered);
    }
  }, [supplierLocations, values.supplier_id]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const loggedInUser = useSelector(selectUser);
  const permissionsDisabled = !(
    loggedInUser?.isSuperAdmin ||
    Boolean(loggedInUser?.admin && import.meta.env.VITE_PERMISSION_EDITABLE_BY_ADMIN === "true") ||
    import.meta.env.VITE_PERMISSION_EDITABLE_BY_ADMIN === "true"
  );

  return (
    <Box height="100%">
      <Box display="flex" mb={4}>
        <Box flex={2}>
          <Typography className={classes._heading} variant="h4">
            {action === "ADD" ? t("New User") : t("Edit User")}
          </Typography>
        </Box>
        <Box flex={2} textAlign="right" position="relative">
          <Box position="absolute" right={0}>
            <FormCancelSaveButton
              disabled={!isValid || isSubmitting}
              onCancel={closeCustomerHandler}
              onSave={handleSubmit}
              ref={{ cancelRef: cancelButtonRef, saveRef: saveButtonRef }}
            />
          </Box>
        </Box>
      </Box>
      <Typography className={classes._subheading} variant="h5">
        {t("Basic Data")}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Autocomplete
            onBlur={handleBlur}
            name="supplier_id"
            label="Supplier"
            errors={errors}
            value={values.supplier_id}
            settings={{
              disableClearable: true,
              valueProp: "id",
              labelProp: "name",
            }}
            onChange={(selected) => {
              handleSupplierChange(selected.id);
            }}
            options={suppliers}
            disabled={action === "EDIT"}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Autocomplete
            onBlur={handleBlur}
            name="supplier_location_id"
            label="Supplier Location"
            errors={errors}
            value={values.supplier_location_id}
            settings={{
              disableClearable: true,
              valueProp: "id",
              labelProp: "name",
            }}
            onChange={(selected) => {
              setFieldValue("supplier_location_id", selected.id);
            }}
            options={filteredLocations}
            disabled={action === "EDIT" || !values.supplier_id}
            required
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Input
            label={t("Name")}
            name="name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.name}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Input
            label={t("Surname")}
            name="surname"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.surname}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Input
            label={t("E-Mail")}
            type="email"
            name="email"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
            errors={errors}
            required
          />
        </Grid>

      </Grid>
      <>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Input
              label={t("Password")}
              type={showPassword ? "text" : "password"}
              name="password"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.password}
              errors={errors}
              required={action !== "EDIT"}
              InputProps={{
                endAdornment: (
                  <Box
                    component="span"
                    onClick={togglePasswordVisibility}
                    style={{
                      cursor: 'pointer',
                      marginRight: '8px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {showPassword ?
                      <VisibilityOffIcon color="action" /> :
                      <VisibilityIcon color="action" />
                    }
                  </Box>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Autocomplete
              onBlur={handleBlur}
              name="role_id"
              label="Role"
              errors={errors}
              value={values.role_id}
              settings={{
                disableClearable: true,
                valueProp: "id",
                labelProp: "name",
              }}
              onChange={(selected) => {
                handleRoleChange(selected.id);
              }}
              options={roles}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3} style={{ marginTop: "0.5rem" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.active}
                  onChange={(event) => setFieldValue("active", event.target.checked)}
                  color="primary"
                  name="active"
                />
              }
              label={t("Active")}
            />
          </Grid>
        </Grid>
        <Box flex={2} mb={4} mt={4} display="flex" alignItems="center">
          <Typography className={classes._heading} variant="h4">
            {t("Permissions")}
          </Typography>
        </Box>
        {!permissions.length ? (
          <CircularProgress />
        ) : (
          <PermissionsManager
            permissions={permissions}
            value={values.permissions || {}}
            onChange={handlePermissionsChange}
            disabled={permissionsDisabled}
          />
        )}
      </>
    </Box>
  );
};
export default UserForm;
